import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchOutputImages, listenForOutputImages, deleteOutputImage, updateOutputImage, rebuildOutputDatabase, fetchFullSizeImage, getOutputDirectoryPath, regenerateThumbnail, regenerateAllVideoThumbnails, getTotalMediaCount } from '../services/comfyOutputService';
import ComfyImageToolbar from './ComfyImageToolbar';
import { useNavigate } from 'react-router-dom';
import { db } from '../db';
import { useAccountContext } from '../context/AccountContext';
import Swal from 'sweetalert2';
import CollectSound from '../sounds/collect.flac';
import md5 from 'blueimp-md5';
import { updateAccount } from '../api-helper';
import { handleCopyToClipboard, handleSaveImage, handleTagImage, handleDescribeImage } from '../utils/momm/MommHandlers';
import { useWindowSize } from '../hooks/useWindowSize';
import { setWorkflowSwitchFlag } from '../utils/workflowUtils';
import eventBus from '../utils/eventBus.js';
import electronBridge from '../utils/electronBridge.js';

// Get electron ipcRenderer for accessing file system paths
// const ipcRenderer = window.require('electron').ipcRenderer;
// const path = window.require('path');
// const isDev = window.require('electron-is-dev');

// SVG Icon Components to replace missing icon files
const VideoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const AudioIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
  </svg>
);

const ImageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const TagIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CollectIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const CollectedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Add a helper function to get a default placeholder SVG for different media types
const getPlaceholderSvg = (mediaType) => {
  switch (mediaType) {
    case 'video':
      return 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>';
    case 'audio':
      return 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" /></svg>';
    default:
      return 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
  }
};

/**
 * ComfyGallery Component
 * A gallery component for displaying ComfyOutput images, videos, and audio files
 */
const ComfyGallery = ({ 
  category = 'all', 
  sortMethod = 'newest', 
  onCategoryChange,
  columnCount = 4,
  onCountUpdate
}) => {
  const { effectiveWidth, isMobile, isTablet } = useWindowSize(true);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [fullSizeImage, setFullSizeImage] = useState(null);
  const [isLoadingFullImage, setIsLoadingFullImage] = useState(false);
  const [isRebuildingDatabase, setIsRebuildingDatabase] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [outputPath, setOutputPath] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(null);
  const [showToolbar, setShowToolbar] = useState(null);
  const observerRef = useRef(null);
  const lastItemRef = useRef(null);
  const navigate = useNavigate();
  const accountContext = useAccountContext();
  
  // Add a function to refresh a specific media item
  const refreshMediaItem = async (hash) => {
    try {
      console.log(`Refreshing media item: ${hash}`);
      
      // Get the updated item with full image data
      const updatedItem = await fetchFullSizeImage(hash);
      
      if (updatedItem) {
        // Update the item in the state
        setMediaItems(prev => prev.map(item => 
          item.hash === hash ? { ...item, image: updatedItem.image } : item
        ));
        
        console.log(`Successfully refreshed media item: ${hash}`);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error(`Error refreshing media item: ${err}`);
      return false;
    }
  };
  
  // Add a function to handle missing thumbnails
  const handleMissingThumbnail = async (item) => {
    if (!item.image && item.hash) {
      console.log(`Attempting to regenerate thumbnail for ${item.name} (${item.hash})`);
      const success = await regenerateThumbnail(item.hash);
      if (success) {
        console.log(`Successfully regenerated thumbnail for ${item.name}`);
        return true;
      } else {
        console.error(`Failed to regenerate thumbnail for ${item.name}`);
      }
    }
    return false;
  };
  
  // Add a function to force refresh the gallery
  const forceRefreshGallery = async () => {
    setIsRebuildingDatabase(true);
    try {
      console.log(`Starting bulk regeneration of video thumbnails...`);
      
      // Call the new bulk regeneration function
      const result = await regenerateAllVideoThumbnails();
      
      if (result.success) {
        console.log(`Successfully regenerated video thumbnails:`, result.results);
        
        // Show success stats
        if (result.results) {
          const { success, error, total } = result.results;
          console.log(`Regenerated ${success} of ${total} video thumbnails (${error} errors)`);
        }
        
        // Reload media items after regeneration
        await loadInitialMedia();
      } else {
        console.error('Failed to regenerate video thumbnails:', result.error);
      }
    } catch (err) {
      console.error('Error during gallery refresh:', err);
    } finally {
      setIsRebuildingDatabase(false);
    }
  };

  // Add a component for missing thumbnails that can regenerate them
  const MissingThumbnail = ({ item }) => {
    const [regenerating, setRegenerating] = useState(false);
    
    useEffect(() => {
      const attemptRegeneration = async () => {
        if (item.hash && !regenerating) {
          setRegenerating(true);
          console.log(`Attempting to regenerate thumbnail for ${item.name} (${item.hash})`);
          const success = await regenerateThumbnail(item.hash);
          
          if (success) {
            console.log(`Successfully regenerated thumbnail for ${item.name}`);
            // Refresh this item in the gallery
            await refreshMediaItem(item.hash);
          } else {
            console.error(`Failed to regenerate thumbnail for ${item.name}`);
          }
          
          setRegenerating(false);
        }
      };
      
      attemptRegeneration();
    }, [item.hash, item.name]);
    
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className="w-16 h-16 text-white">
          <VideoIcon />
        </div>
        {regenerating && (
          <div className="mt-2 text-white text-xs">Regenerating thumbnail...</div>
        )}
      </div>
    );
  };
  
  // Get the comfy output path on component mount
  useEffect(() => {
    const getOutputPath = async () => {
      const path = await getOutputDirectoryPath();
      if (path) {
        setOutputPath(path);
      }
    };
    
    getOutputPath();
  }, []);
  
  // Load media items on component mount or when category/sortMethod changes
  useEffect(() => {
    console.log("Category or sort method changed, loading initial media");
    loadInitialMedia();
    
    // Reset state when category or sortMethod changes
    return () => {
      setMediaItems([]);
      setCurrentPage(1);
      setHasMore(true);
      setIsInitialLoad(true);
    };
  }, [category, sortMethod]);
  
  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (loading || !hasMore) return;
    
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };
    
    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        loadMoreMedia();
      }
    }, options);
    
    if (lastItemRef.current) {
      observerRef.current.observe(lastItemRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, mediaItems]);
  
  // Load initial batch of media items
  const loadInitialMedia = async () => {
    setIsInitialLoad(true);
    setLoading(true);
    try {
      // First, let's get the actual total count directly
      try {
        console.log("Fetching direct total count first");
        const actualTotalCount = await getTotalMediaCount(category);
        console.log("Direct total count result:", actualTotalCount);
        
        if (onCountUpdate && actualTotalCount > 0) {
          console.log("Updating with direct total count:", actualTotalCount);
          onCountUpdate({
            totalItems: 0, // Not relevant since we only display total
            totalStoredItems: actualTotalCount
          });
        }
      } catch (countError) {
        console.error("Error getting direct count:", countError);
      }

      // Then load the visible items
      const initialMedia = await fetchOutputImages(1, category, sortMethod);
      if (initialMedia) {
        // Handle either a single object or an array of objects
        const mediaArray = Array.isArray(initialMedia) ? initialMedia : [initialMedia];
        
        // Deduplicate by hash
        const uniqueMedia = [];
        const seenHashes = new Set();
        
        for (const item of mediaArray) {
          if (!seenHashes.has(item.hash)) {
            seenHashes.add(item.hash);
            uniqueMedia.push(item);
          }
        }
        
        setMediaItems(uniqueMedia);
        setHasMore(mediaArray.length >= 20); // Assuming page size is 20
      } else {
        setMediaItems([]);
        setHasMore(false);
        
        // Update with zero
        if (onCountUpdate) {
          onCountUpdate({
            totalItems: 0,
            totalStoredItems: 0
          });
        }
      }
    } catch (error) {
      console.error('Error loading media:', error);
      // Update with zero on error
      if (onCountUpdate) {
        onCountUpdate({
          totalItems: 0,
          totalStoredItems: 0
        });
      }
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
      
      // Try one more time after a delay to get a more accurate count
      setTimeout(async () => {
        try {
          const refreshedCount = await getTotalMediaCount(category);
          console.log("Refreshed total count after delay:", refreshedCount);
          
          if (onCountUpdate && refreshedCount > 0) {
            console.log("Updating with refreshed count:", refreshedCount);
            onCountUpdate({
              totalItems: mediaItems.length,
              totalStoredItems: refreshedCount
            });
          }
        } catch (err) {
          console.error("Error getting refreshed count:", err);
        }
      }, 1000);
    }
  };
  
  // Load more media items when scrolling
  const loadMoreMedia = async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    try {
      const nextPage = currentPage + 1;
      const moreMedia = await fetchOutputImages(nextPage, category, sortMethod);
      
      if (moreMedia) {
        const mediaArray = Array.isArray(moreMedia) ? moreMedia : [moreMedia];
        
        if (mediaArray.length > 0) {
          // Deduplicate against existing items and within new items
          const newItems = [];
          const existingHashes = new Set(mediaItems.map(item => item.hash));
          const newHashes = new Set();
          
          for (const item of mediaArray) {
            if (!existingHashes.has(item.hash) && !newHashes.has(item.hash)) {
              newHashes.add(item.hash);
              newItems.push(item);
            }
          }
          
          if (newItems.length > 0) {
            setMediaItems(prev => [...prev, ...newItems]);
            setCurrentPage(nextPage);
            setHasMore(mediaArray.length >= 20); // Assuming page size is 20
          } else {
            setHasMore(false);
          }
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more media:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle selecting an item for detailed view
  const handleSelectItem = async (item) => {
    setSelectedItem(item);
    setFullSizeImage(null);
    setIsLoadingFullImage(true);
    
    try {
      // Fetch the full-size image only when needed
      const fullImage = await fetchFullSizeImage(item.hash);
      if (fullImage) {
        setFullSizeImage(fullImage);
      }
    } catch (error) {
      console.error('Error loading full-size image:', error);
    } finally {
      setIsLoadingFullImage(false);
    }
  };
  
  // Handle closing the detail view
  const handleCloseDetailView = () => {
    setSelectedItem(null);
    setFullSizeImage(null);
  };
  
  // Handle deleting a media item
  const handleDelete = async (hash, event) => {
    event.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const success = await deleteOutputImage(hash);
        if (success) {
          if (selectedItem && selectedItem.hash === hash) {
            handleCloseDetailView();
          }
          setMediaItems(prev => prev.filter(item => item.hash !== hash));
        }
      } catch (error) {
        console.error('Error deleting media:', error);
      }
    }
  };
  
  // Handle toggling collection status - REVISED LOGIC
  const handleToggleCollection = async (mediaItem, event) => {
    event.stopPropagation();

    if (!accountContext.loggedIn) {
      Swal.fire({
        title: "Not Logged In",
        text: "You need to be logged in to add images to your collection. Please login to your account to proceed.",
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#FFB02E",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/profile');
        }
      });
      return;
    }

    await db.settings.put({ key: 'toggleLock', value: true });
    const collectAudio = new Audio(CollectSound);
    const soundSettingCheck = await db.settings.where('key').equals('play_sounds').first();

    try {
      const currentlyInCollection = mediaItem.inCollection;
      const newInCollectionStatus = !currentlyInCollection;

      // 1. Update the Comfy output DB (this marks the item in the comfy_output.db)
      const comfyDbUpdateSuccess = await updateOutputImage({ ...mediaItem, inCollection: newInCollectionStatus });

      if (comfyDbUpdateSuccess) {
        // 2. Update local state for immediate UI feedback
        const updatedMediaItem = { ...mediaItem, inCollection: newInCollectionStatus };
        setMediaItems(prev =>
          prev.map(item =>
            item.hash === mediaItem.hash ? updatedMediaItem : item
          )
        );
        if (selectedItem && selectedItem.hash === mediaItem.hash) {
          setSelectedItem(updatedMediaItem);
        }

        // 3. Interact with user's collection database (db.collectedImages) and backend
        if (newInCollectionStatus) { // Adding to collection
          // First check if the image already exists in the collection
          const existingImage = await db.collectedImages.where('hash').equals(mediaItem.hash).first();
          
          if (existingImage) {
            console.log("Image already exists in collection, skipping DB insert");
          } else {
            let collectionImageCount;
            await db.collectedImages.count((count) => {
              collectionImageCount = count + 1;
            });
            
            const uidForCollectedImage = mediaItem.hash;

            const collectedImageEntry = {
              uid: uidForCollectedImage,
              imageID: collectionImageCount,
              image: mediaItem.image,
              seed: mediaItem.seed_value || mediaItem.seed || (mediaItem.params ? mediaItem.params.seed_value : null),
              model: mediaItem.model || (mediaItem.params ? mediaItem.params.model : null),
              hash: mediaItem.hash,
              online: false,
              tag: mediaItem.tag || "",
              prompt: mediaItem.prompt || (mediaItem.metadata ? mediaItem.metadata.prompt : ""),
              params: mediaItem.params || (mediaItem.metadata ? mediaItem.metadata.params : null),
              public: false,
              name: mediaItem.name,
              data_type: mediaItem.data_type,
              timestamp: mediaItem.timestamp || Date.now(),
              workflow: mediaItem.workflow,
            };
            
            await db.collectedImages.put(collectedImageEntry);
            console.log("Added image to local collection DB:", collectedImageEntry);
          }

          // Always update the backend regardless of local DB status
          const imageArrForApi = [{
            uid: mediaItem.hash,
            image: mediaItem.image,
            seed: mediaItem.seed_value || mediaItem.seed || (mediaItem.params ? mediaItem.params.seed_value : null),
            model: mediaItem.model || (mediaItem.params ? mediaItem.params.model : null),
            hash: mediaItem.hash,
            online: false,
            tag: mediaItem.tag || "",
            prompt: mediaItem.prompt || (mediaItem.metadata ? mediaItem.metadata.prompt : ""),
          }];

          const colArrForApi = [{
            id: await db.collectedImages.count(),
            params: mediaItem.params || "{}",
            accountNo: accountContext.accountDetails.accountNo,
            timestamp: Date.now(),
          }];
          
          // Filter out undefined values
          const cleanImageArrForApi = imageArrForApi.map(img => 
            Object.fromEntries(Object.entries(img).filter(([_, v]) => v !== undefined))
          );
          const cleanColArrForApi = colArrForApi.map(col =>
            Object.fromEntries(Object.entries(col).filter(([_, v]) => v !== undefined))
          );

          await updateAccount(
            JSON.stringify(cleanImageArrForApi), 
            accountContext.accountDetails.accountNo, 
            null,
            JSON.stringify(cleanColArrForApi)
          );
          console.log("Synced add to collection with backend.");
        } else { // Removing from collection
          // OutputImage.jsx uses props.hash for deleteCollectionImage.
          // We should use mediaItem.hash. It also deletes from db.collectedImages using image.
          // The UID used in db.collectedImages for Comfy items might be the item.hash.
          const uidForCollectedImage = mediaItem.hash;
          await db.collectedImages.where('uid').equals(uidForCollectedImage).delete();
          
          // Assuming deleteCollectionImage API helper is available and works with accountNo and image hash
          // This part is commented out in OutputImage.jsx but good to have if backend sync is needed for removal
          // await deleteCollectionImage(accountContext.accountDetails.accountNo, mediaItem.hash);
          console.log("Removed image from local collection DB and (potentially) backend.");
        }

        if (soundSettingCheck && soundSettingCheck.value && newInCollectionStatus) {
          collectAudio.loop = false;
          collectAudio.volume = 0.8;
          collectAudio.play();
        }
        
        // Optionally, refresh the entire gallery or just rely on local state update
        // await loadInitialMedia(); // Or specific item refresh if available
      } else {
        console.error('Failed to update comfyDB inCollection status for item:', mediaItem.hash);
        // Revert UI changes if needed or show error
        Swal.fire("Error", "Could not update collection status in ComfyDB.", "error");
      }
    } catch (error) {
      console.error('Error toggling collection status:', error);
      Swal.fire("Error", `An error occurred: ${error.message}`, "error");
    } finally {
      await db.settings.put({ key: 'toggleLock', value: false });
    }
  };
  
  // Handle rebuilding the database
  const handleRebuildDatabase = async () => {
    if (isRebuildingDatabase) return;
    
    setIsRebuildingDatabase(true);
    try {
      const result = await rebuildOutputDatabase();
      if (result.success) {
        await loadInitialMedia();
      }
    } catch (error) {
      console.error('Error rebuilding database:', error);
    } finally {
      setIsRebuildingDatabase(false);
    }
  };
  
  // Handle category change
  const handleCategoryChange = (newCategory) => {
    if (onCategoryChange) {
      onCategoryChange(newCategory);
    }
  };
  
  // Handle clicking on a video in the gallery
  const handleVideoClick = (item, event) => {
    event.stopPropagation();
    if (playingVideo === item.hash) {
      // If already playing, stop it
      setPlayingVideo(null);
    } else {
      // Start playing this video
      setPlayingVideo(item.hash);
    }
  };
  
  // Add a function to handle using the image for generation
  // const handleUseImage = async (item) => {
  //   try {
  //     console.log(`Using image for generation: ${item.hash}`);
      
  //     // Clear any previous image data in localStorage
  //     localStorage.removeItem('image_transfer_active');
  //     localStorage.removeItem('temp_image_data');
  //     localStorage.removeItem('image_transfer_timestamp');
  //     localStorage.removeItem('selected_image');
  //     localStorage.removeItem('selected_image_hash');
  //     localStorage.removeItem('transfer_image_object');
      
  //     // Set workflow switch flag to indicate image selection requires workflow check
  //     // Store current workflow/model info if available
  //     const currentWorkflow = localStorage.getItem('current_workflow');
  //     const currentModel = localStorage.getItem('current_model');
      
  //     if (currentWorkflow || currentModel) {
  //       setWorkflowSwitchFlag({
  //         workflow: currentWorkflow,
  //         model: currentModel,
  //         timestamp: Date.now()
  //       });
  //     } else {
  //       // Just set the flag without original workflow info
  //     setWorkflowSwitchFlag(null);
  //     }
      
  //     // Process the image data properly
  //     if (item.image) {
  //       console.log("Using available image data from item");
        
  //       // Check if the image is already a string (likely base64)
  //       if (typeof item.image === 'string') {
  //         console.log("Image is already a string (likely base64)");
          
  //         // Create a properly formatted data URL
  //         const formattedImageData = item.image.startsWith('data:') 
  //           ? item.image 
  //           : `data:image/png;base64,${item.image}`;
            
  //         // Store in multiple formats for maximum compatibility
  //         localStorage.setItem('image_transfer_active', 'true');
  //         localStorage.setItem('temp_image_data', formattedImageData);
  //         localStorage.setItem('image_transfer_timestamp', Date.now().toString());
          
  //         // Also use the ComfyOutput style format
  //         localStorage.setItem('selected_image', item.image);
  //         localStorage.setItem('selected_image_hash', item.hash);
          
  //         // Create a structured object with all data
  //         const imageObject = {
  //           image: formattedImageData,
  //           hash: item.hash,
  //           uid: `comfy-${Date.now()}`,
  //           prompt: item.prompt || '',
  //           seed: item.seed_value || '',
  //           model: item.model || ''
  //         };
          
  //         localStorage.setItem('transfer_image_object', JSON.stringify(imageObject));
  //         localStorage.setItem('selected_image_prompt', item.prompt || '');
          
  //         // Show success message
  //         console.log("Image data stored successfully");
          
  //         // First select the image-capable model
  //         eventBus.emit('selectModel', {
  //           modelName: '(geminiimages) Nano Banana 2.5 Flash',
  //           openSettings: true
  //         });
          
  //         // Navigate to Generate page after a short delay
  //         setTimeout(() => {
  //           navigate('/generate');
  //         }, 100);
  //       } 
  //       // Handle Blob or ArrayBuffer data
  //       else if (item.image instanceof Blob || item.image instanceof ArrayBuffer) {
  //         console.log("Image is a Blob or ArrayBuffer, converting to base64");
          
  //         // Convert to base64 asynchronously
  //         const reader = new FileReader();
  //         reader.onloadend = function() {
  //           const base64data = reader.result;
            
  //           // Store in multiple formats for maximum compatibility
  //           localStorage.setItem('image_transfer_active', 'true');
  //           localStorage.setItem('temp_image_data', base64data);
  //           localStorage.setItem('image_transfer_timestamp', Date.now().toString());
            
  //           // Extract just the base64 part for the ComfyOutput format
  //           const base64Only = base64data.split(',')[1];
  //           localStorage.setItem('selected_image', base64Only);
  //           localStorage.setItem('selected_image_hash', item.hash);
            
  //           // Create a structured object with all data
  //           const imageObject = {
  //             image: base64data,
  //             hash: item.hash,
  //             uid: `comfy-${Date.now()}`,
  //             prompt: item.prompt || '',
  //             seed: item.seed_value || '',
  //             model: item.model || ''
  //           };
            
  //           localStorage.setItem('transfer_image_object', JSON.stringify(imageObject));
  //           localStorage.setItem('selected_image_prompt', item.prompt || '');
            
  //           // Show success message
  //           console.log("Blob/ArrayBuffer converted and stored");
            
  //           // First select the image-capable model
  //           eventBus.emit('selectModel', {
  //             modelName: '(geminiimages) Nano Banana 2.5 Flash',
  //             openSettings: true
  //           });
            
  //           // Navigate to Generate page after a short delay
  //           setTimeout(() => {
  //             navigate('/generate');
  //           }, 100);
  //         };
          
  //         if (item.image instanceof ArrayBuffer) {
  //           reader.readAsDataURL(new Blob([item.image]));
  //         } else {
  //           reader.readAsDataURL(item.image);
  //         }
  //         return; // Early return since we're handling this asynchronously
  //       }
  //       else {
  //         console.error("Unknown image data type:", typeof item.image);
  //         throw new Error("Unable to process image data format");
  //       }
  //     } else {
  //       // If no thumbnail is available, try to fetch the full image first
  //       console.log("No image data available, fetching full image");
  //       setIsLoadingFullImage(true);
  //       const fullImage = await fetchFullSizeImage(item.hash);
  //       setIsLoadingFullImage(false);
        
  //       if (fullImage && fullImage.image) {
  //         console.log("Successfully fetched full image");
          
  //         // Create a properly formatted data URL
  //         const formattedImageData = typeof fullImage.image === 'string' && fullImage.image.startsWith('data:')
  //           ? fullImage.image
  //           : `data:image/png;base64,${fullImage.image}`;
          
  //         // Store in multiple formats for maximum compatibility
  //         localStorage.setItem('image_transfer_active', 'true');
  //         localStorage.setItem('temp_image_data', formattedImageData);
  //         localStorage.setItem('image_transfer_timestamp', Date.now().toString());
          
  //         // Also use the ComfyOutput style format
  //         localStorage.setItem('selected_image', typeof fullImage.image === 'string' ? fullImage.image : '');
  //         localStorage.setItem('selected_image_hash', item.hash);
  //         localStorage.setItem('selected_image_prompt', item.prompt || '');
          
  //         console.log("Full image data stored");
          
  //         // First select the image-capable model
  //         eventBus.emit('selectModel', {
  //           modelName: '(geminiimages) Nano Banana 2.5 Flash',
  //           openSettings: true
  //         });
          
  //         // Navigate to Generate page after a short delay
  //         setTimeout(() => {
  //           navigate('/generate');
  //         }, 100);
  //       } else {
  //         console.error('Could not load image for generation');
  //         throw new Error("Failed to fetch image data");
  //       }
  //     }
  //   } catch (err) {
  //     console.error(`Error using image for generation:`, err);
      
  //     // Show error notification
  //     const errorToast = document.createElement('div');
  //     errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
  //     errorToast.textContent = `Error using image: ${err.message}`;
  //     document.body.appendChild(errorToast);
      
  //     setTimeout(() => {
  //       errorToast.style.opacity = '0';
  //       setTimeout(() => {
  //         if (document.body.contains(errorToast)) {
  //           document.body.removeChild(errorToast);
  //         }
  //       }, 300);
  //     }, 2000);
  //   }
  // };
  const handleUseImage = async (item) => {
    console.log('[ComfyGallery] handleUseImage called for item:', item);
    let imageData = item.image;
    
    // Check if image is a URL (http/https)
    if (item.image.startsWith('http://') || item.image.startsWith('https://')) {
        try {
            console.log('[ComfyGallery] Converting URL to base64 for API call');
            
            // Fetch the image from the URL
            const response = await fetch(item.image);
            const blob = await response.blob();
            
            // Convert blob to base64
            const reader = new FileReader();
            imageData = await new Promise((resolve, reject) => {
                reader.onloadend = () => {
                    // Remove the data:image/xxx;base64, prefix and return only base64 string
                    const base64String = reader.result.split(',')[1];
                    resolve(base64String);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            
            console.log('[ComfyGallery] Successfully converted URL to base64');
        } catch (error) {
            console.error('[ComfyGallery] Error converting URL to base64:', error);
            // Fall back to using the original URL
            return;
        }
    }
    // If in Electron and image is a file path, read the file and convert to base64
    else if (electronBridge.isElectron && (item.image.includes('://') || item.image.includes(':\\'))) {
        try {
            const fs = window.require('fs');
            const path = window.require('path');
            
            // Clean up the file path (remove file:// prefix if present)
            let filePath = item.image;
            if (filePath.startsWith('file://')) {
                filePath = filePath.replace('file://', '');
            }
            if (filePath.startsWith('safe-file://')) {
                filePath = decodeURI(filePath.replace('safe-file://', ''));
            }
            
            // Read the file as buffer
            const buffer = fs.readFileSync(filePath);
            
            // Convert buffer to base64
            imageData = buffer.toString('base64');
            
            console.log('[ComfyGallery] Converted file to base64 for useImage');
        } catch (error) {
            console.error('[ComfyGallery] Error reading file for useImage:', error);
            // Fall back to using the original path
            imageData = item.image;
        }
    }
    
    // Emit event for DynamicPromptForm to receive the image
    eventBus.emit('useImage', { 
        image: imageData,
        uid: item.uid 
    });
}
  // Update the mouse enter/leave handlers to control toolbar visibility
  const handleMouseEnter = (item) => {
    setHoveredItem(item.hash);
    setShowToolbar(item.hash);
    if (item.data_type === 'video') {
      setPlayingVideo(item.hash);
    } else if (item.data_type === 'audio') {
      setPlayingAudio(item.hash);
    }
  };

  // Handle mouse leave on item
  const handleMouseLeave = (item) => {
    setHoveredItem(null);
    setShowToolbar(null);
    if (item.data_type === 'video') {
      setPlayingVideo(null);
    } else if (item.data_type === 'audio') {
      setPlayingAudio(null);
    }
  };
  
  // Handle showing item details
  const handleShowDetails = (item, e) => {
    e.stopPropagation();
    setShowDetailsDialog(item);
  };

  // Handle closing details dialog
  const closeDetailsDialog = (e) => {
    if (e) e.stopPropagation();
    setShowDetailsDialog(null);
  };
  
  // Updated renderMediaItem function to handle null images
  const renderMediaItem = (item, isDetailView = false) => {
    // For detail view, use the full-size image if available for images only
    const imageToUse = isDetailView && fullSizeImage && item.data_type === 'image' 
      ? fullSizeImage.image 
      : item.image;
    
    // Create file URL for videos and audio
    const getFileUrl = (filename) => {
      if (!outputPath) return '';
      // Use custom protocol for secure local file access
      return `localfile://${outputPath}/${filename}`;
    };
    
    switch (item.data_type) {
      case 'image':
        // Check if it's a WebP image
        const isWebP = item.name && item.name.toLowerCase().endsWith('.webp');
        
        if (isWebP) {
          // Use direct file URL for WebP to support animation
          return (
            <img 
              src={getFileUrl(item.name)} 
              alt={item.prompt || 'Generated image'} 
              className={`w-full h-full ${isDetailView ? 'object-contain' : 'object-cover'} bg-black`}
              loading="lazy"
              onError={(e) => {
                console.error('Error loading WebP image:', e);
                // Fallback to base64 thumbnail if direct file access fails
                e.target.src = imageToUse ? `data:image/webp;base64,${imageToUse}` : getPlaceholderSvg('image');
              }}
            />
          );
        }
        
        // For regular images, use base64 data
        return (
          <img 
            src={imageToUse ? `data:image/png;base64,${imageToUse}` : getPlaceholderSvg('image')} 
            alt={item.prompt || 'Generated image'} 
            className="w-full h-full object-cover bg-black"
            loading="lazy"
            onError={(e) => {
              console.error('Error loading image:', e);
              e.target.src = getPlaceholderSvg('image');
            }}
          />
        );
      case 'video':
        if (isDetailView) {
          // In detail view, show video with direct file access
          return (
            <div className="relative w-full h-full flex items-center justify-center">
              <video 
                src={getFileUrl(item.name)}
                className="max-w-full max-h-[80vh] object-contain"
                controls
                autoPlay
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  console.error('Error playing video in detail view:', e);
                }}
              />
            </div>
          );
        } else {
          // In gallery view, show thumbnail with play button overlay or playing video
          return (
            <div 
              className="relative w-full h-full bg-black"
              // Remove onClick video toggle for hover-to-play, or keep if click-to-play-fullscreen is desired
              // onClick={(e) => handleVideoClick(item, e)} 
            >
              {/* Thumbnail image - use base64 only for the small thumbnail */}
              {playingVideo !== item.hash && imageToUse && (
                <img 
                  src={`data:image/jpeg;base64,${imageToUse}`} 
                  alt={item.name || 'Video thumbnail'} 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    console.error('Error loading video thumbnail:', e);
                    e.target.src = getPlaceholderSvg('video');
                    handleMissingThumbnail(item).then(success => {
                      if (success) {
                        refreshMediaItem(item.hash);
                      }
                    });
                  }}
                />
              )}
              {playingVideo !== item.hash && !imageToUse && (
                <MissingThumbnail item={item} />
              )}
              
              {/* Play button overlay - shown only if video is NOT playing on hover */}
              {playingVideo !== item.hash && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className={`w-12 h-12 text-white opacity-70`}>
                          <VideoIcon />
                      </div>
                  </div>
              )}
              
              {/* Video player (shown only when playing on hover) */}
              {playingVideo === item.hash && (
                <div className="absolute inset-0 z-10 bg-black">
                  <video 
                    className="w-full h-full object-contain"
                    src={getFileUrl(item.name)}
                    autoPlay 
                    muted // Essential for hover-to-play UX
                    loop // Optional: if you want videos to loop
                    // controls // Optional: remove controls for seamless hover play
                    onEnded={() => { /* Can add logic here if needed, e.g., replay or show thumbnail */ }}
                    onError={(e) => {
                      console.error('Error playing video with file path:', e);
                      // Fallback or error display if video fails
                      setPlayingVideo(null); // Stop trying to play if error
                    }}
                    onPlay={(e) => {
                      // Handle play event to catch any autoplay errors
                      const video = e.target;
                      if (video.paused) {
                        video.play().catch(error => {
                          if (error.name !== 'AbortError') {
                            console.error('Video autoplay error:', error);
                          }
                        });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          );
        }
      case 'audio':
        if (isDetailView) {
          // In detail view, show audio with controls and icon
          return (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800">
              <div className="w-32 h-32 text-white mb-8">
                <AudioIcon />
              </div>
              <audio 
                src={getFileUrl(item.name)}
                controls
                className="w-3/4"
                autoPlay
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  console.error('Error playing audio:', e);
                }}
                onPlay={(e) => {
                  // Handle play event to catch any autoplay errors
                  const audio = e.target;
                  if (audio.paused) {
                    audio.play().catch(error => {
                      if (error.name !== 'AbortError') {
                        console.error('Audio autoplay error:', error);
                      }
                    });
                  }
                }}
              />
            </div>
          );
        } else {
          // In gallery view, show thumbnail or playing audio on hover
          return (
            <div className="relative w-full h-full bg-gray-800 flex items-center justify-center">
              {playingAudio !== item.hash && (
                <>
                  <img 
                    src={imageToUse ? `data:image/jpeg;base64,${imageToUse}` : getPlaceholderSvg('audio')} 
                    alt={item.name || 'Audio thumbnail'} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = getPlaceholderSvg('audio');
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-12 h-12 text-white opacity-70">
                      <AudioIcon />
                    </div>
                  </div>
                </>
              )}
              {playingAudio === item.hash && (
                <audio 
                  src={getFileUrl(item.name)}
                  className="w-full h-full"
                  autoPlay
                  loop // Optional: if you want audio to loop
                  controls // Show controls for volume/unmute
                  onEnded={() => { /* Optional: handle audio end */ }}
                  onError={(e) => {
                    console.error('Error playing audio on hover:', e);
                    setPlayingAudio(null); // Stop trying to play if error
                  }}
                  onPlay={(e) => {
                    // Handle play event to catch any autoplay errors
                    const audio = e.target;
                    if (audio.paused) {
                      audio.play().catch(error => {
                        if (error.name !== 'AbortError') {
                          console.error('Audio autoplay error:', error);
                        }
                      });
                    }
                  }}
                />
              )}
            </div>
          );
        }
      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <div className="w-12 h-12 text-gray-500">
              <ImageIcon />
            </div>
          </div>
        );
    }
  };
  
  // Add cleanup function for videos and audio when component unmounts
  useEffect(() => {
    return () => {
      // Stop any playing videos when unmounting
      setPlayingVideo(null);
      setPlayingAudio(null); // Stop any playing audio when unmounting
    };
  }, []);
  
  // Clean up effects to avoid redundant updates
  useEffect(() => {
    return () => {
      // Clean up any pending operations
      console.log("Cleaning up ComfyGallery component");
    };
  }, []);

  return (
    <div className="comfy-gallery w-full">
      {isInitialLoad ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
        </div>
      ) : mediaItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-lg text-gray-300 mb-4">No media items found</p>
          <button 
            className="px-4 py-2 bg-accent-blue text-white rounded-md"
            onClick={handleRebuildDatabase}
            disabled={isRebuildingDatabase}
          >
            {isRebuildingDatabase ? 'Rebuilding...' : 'Rebuild Database'}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap w-full gap-0 px-2 sm:px-4 justify-center mx-auto" style={{ margin: '-4px' }}>
          {mediaItems.map((item, index) => (
            <div 
              key={item.hash} 
              ref={index === mediaItems.length - 1 ? lastItemRef : null}
              className={`relative group shadow-md cursor-pointer border-2 sm:border-4 ${item.data_type === 'video' ? 'border-blue-500' : item.data_type === 'audio' ? 'border-green-500' : 'border-orange-500'} bg-medium-blue rounded-lg sm:rounded-xl aspect-square overflow-visible`}
              style={{ 
                width: isMobile ? `calc(${100/Math.min(columnCount, 2)}% - 8px)` : 
                       isTablet ? `calc(${100/Math.min(columnCount, 3)}% - 12px)` :
                       effectiveWidth < 1024 ? `calc(${100/Math.min(columnCount, 4)}% - 16px)` :
                       `calc(${100/columnCount}% - 16px)`, 
                margin: isMobile ? '4px' : 
                        isTablet ? '6px' : '8px' 
              }}
              onClick={() => handleSelectItem(item)}
              onMouseEnter={() => handleMouseEnter(item)}
              onMouseLeave={() => handleMouseLeave(item)}
            >
              {/* Show toolbar for images when the item is hovered - use JavaScript state instead of CSS hover */}
              {item.data_type === 'image' && showToolbar === item.hash && (
                <div 
                  className="absolute top-0 left-0 right-0 z-40 transform -translate-y-full transition-all duration-300"
                  onMouseEnter={() => setShowToolbar(item.hash)}
                  onMouseLeave={() => setShowToolbar(null)}
                >
                  <ComfyImageToolbar 
                    item={item}
                    inCollection={item.inCollection}
                    onToggleCollection={handleToggleCollection}
                    onDelete={handleDelete}
                    onZoom={() => handleSelectItem(item)}
                    onUseImage={handleUseImage}
                    onCopyToClipboard={() => handleCopyToClipboard(item.image)}
                    onSaveImage={() => handleSaveImage(item.image, item.name || `comfy_image_${item.hash?.substring(0, 8)}`)}
                    onTagImage={() => handleTagImage(item.image, item.hash)}
                    onDescribeImage={() => handleDescribeImage(item.image, item.hash)}
                  />
                </div>
              )}
              
              {/* This inner div will handle the overflow and rounding for the image content */}
              <div className="relative w-full h-full overflow-hidden rounded-xl">
                {/* Render media item */}
                {renderMediaItem(item)}
                
                {/* Model information - Ensure this is only for images if it exists */}
                {item.data_type === 'image' && item.model ? (
                  <div className="absolute top-0 right-0 px-3 py-2 bg-dark-blue/60 text-white text-sm transition-opacity duration-200 group-hover:opacity-0">
                    {item.model}
                  </div>
                ) : item.data_type === 'image' ? (
                  <div className="absolute top-0 right-0 px-3 py-2 bg-dark-blue/60 text-white text-sm transition-opacity duration-200 group-hover:opacity-0">
                    stable-diffusion-v1-5
                  </div>
                ) : null}
                
                {/* Tag display */}
                {item.tag && (
                  <div className="absolute bottom-0 left-0 px-3 py-2 bg-dark-blue/40 text-white text-sm">
                    {item.tag}
                  </div>
                )}
                
                {/* Dimensions and seed display - Ensure this is only for images if it exists */}
                {item.data_type === 'image' && (item.width && item.height || item.seed_value) && (
                  <div className="absolute bottom-0 right-0 flex flex-col items-end transition-opacity duration-200 group-hover:opacity-0">
                    {item.seed_value && (
                      <div className="px-3 py-1 bg-dark-blue/60 text-white text-sm">
                        Seed: {item.seed_value}
                      </div>
                    )}
                    {item.width && item.height ? (
                      <div className="px-3 py-1 bg-dark-blue/40 text-white text-sm">
                        {item.width} x {item.height}
                      </div>
                    ) : item.data_type === 'image' ? ( // Fallback for image if specific dimensions are missing
                      <div className="px-3 py-1 bg-dark-blue/40 text-white text-sm">
                        512 x 512
                      </div>
                    ) : null}
                  </div>
                )}
                
                {/* View count/image ID at bottom - always visible */}
                {item.data_type === 'image' && (
                  <div className="absolute bottom-0 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-row items-center bg-dark-blue/70 rounded-tr-lg rounded-bl-lg z-30">
                    <div className="hover:bg-dark-blue/90 p-2 transition cursor-pointer" onClick={(e) => {
                      e.stopPropagation();
                      handleShowDetails(item, e);
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <span className="text-xs font-light px-2 py-2 cursor-pointer hover:text-yellow transition" onClick={(e) => {
                      e.stopPropagation();
                      handleShowDetails(item, e);
                    }}>
                      #{item.hash ? item.hash.substring(0, 9) : '229108426'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {loading && !isInitialLoad && (
        <div className="flex justify-center items-center py-4 mt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
        </div>
      )}
      
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={handleCloseDetailView}>
          <div className={`bg-dark-blue rounded-lg ${selectedItem.data_type === 'image' ? 'max-w-4xl w-full' : 'max-w-3xl w-full'} max-h-[90vh] overflow-hidden`} onClick={(e) => e.stopPropagation()}>
            {selectedItem.data_type === 'image' ? (
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-2/3 bg-black flex items-center justify-center relative min-h-[300px]">
                  {isLoadingFullImage ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
                    </div>
                  ) : null}
                  <div className={isLoadingFullImage ? 'opacity-50' : ''}>
                    {renderMediaItem(fullSizeImage || selectedItem, true)}
                  </div>
                </div>
                <div className="w-full md:w-1/3 p-4 overflow-y-auto max-h-[90vh] text-white">
                  <h2 className="text-xl font-bold mb-2">{selectedItem.name}</h2>
                  
                  <div className="flex flex-col space-y-4 p-4">
                    <div className="text-sm text-gray-300">
                      {selectedItem.prompt || 'No prompt available'}
                    </div>
                    {selectedItem.seed_value && (
                      <div className="text-sm text-gray-400">
                        Seed: {selectedItem.seed_value}
                      </div>
                    )}
                    {selectedItem.model && (
                      <div className="text-sm text-gray-400">
                        Model: {selectedItem.model}
                      </div>
                    )}
                    {selectedItem.params && (
                      <div className="text-sm text-gray-400">
                        <h3 className="font-semibold mb-2">Parameters</h3>
                        <div>
                          {Object.entries(typeof selectedItem.params === 'string' ? JSON.parse(selectedItem.params) : selectedItem.params).map(([key, value]) => (
                            value && <div key={key}><span className="font-medium">{key}:</span> {value}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full bg-black flex items-center justify-center relative" style={{ height: 'calc(90vh - 40px)' }}>
                {renderMediaItem(selectedItem, true)}
                
                {/* Close button */}
                <button 
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-800 bg-opacity-70 flex items-center justify-center text-white hover:bg-opacity-100"
                  onClick={handleCloseDetailView}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Image Details Dialog */}
      {showDetailsDialog && (
        <div className="fixed inset-y-0 right-0 z-50 w-80 bg-[#1e1147] shadow-xl">
          <div className="flex justify-between items-center p-4">
            <h3 className="text-xl font-bold text-amber-500">{showDetailsDialog.data_type.charAt(0).toUpperCase() + showDetailsDialog.data_type.slice(1)} Details:</h3>
            <button 
              className="text-white hover:text-amber-500 text-2xl"
              onClick={closeDetailsDialog}
            >
              ×
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <span className="font-medium text-amber-500">Prompt:</span> <span className="text-white">{showDetailsDialog.prompt || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Negative Prompt:</span> <span className="text-white">{showDetailsDialog.negative_prompt || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Seed:</span> <span className="text-white">{showDetailsDialog.seed_value || (showDetailsDialog.params && showDetailsDialog.params.seed_value) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Steps:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.num_inference_steps) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Guidance:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.guidance_scale) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Model:</span> <span className="text-white">{showDetailsDialog.model || (showDetailsDialog.params && showDetailsDialog.params.model) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Height x Width:</span> <span className="text-white">{showDetailsDialog.height || (showDetailsDialog.params && showDetailsDialog.params.height) ? (showDetailsDialog.height || (showDetailsDialog.params && showDetailsDialog.params.height)) + ' x ' + (showDetailsDialog.width || (showDetailsDialog.params && showDetailsDialog.params.width)) : ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Scheduler:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.scheduler) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Lora:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.lora) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Lora Scale:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.lora_scale) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Count:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.count) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Image Batch Size:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.image_batch_size) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Control Net:</span> <span className="text-white">{(showDetailsDialog.params && showDetailsDialog.params.control_net) || ''}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComfyGallery;